import { Sparkles, Send } from "lucide-react";
import AICrystal from "@/components/three/AICrystal";

const CAPS = [
  { title: "Ticket Risk Scoring", desc: "Each ticket gets a risk score based on work type, location history, customer, urgency, and complexity. High-risk tickets surface automatically." },
  { title: "Smart Summaries", desc: "AI summarizes ticket notes, field updates, and locator activity so supervisors don't read every entry." },
  { title: "Next Best Action", desc: "Suggested next steps for overdue tickets, incomplete closeouts, or at-risk SLAs." },
  { title: "Auto-Flag Engine", desc: "Missing photos, incomplete notes, overdue windows, abnormal production, compliance risk — all caught automatically." },
  { title: "Supervisor Intelligence", desc: "Ask LocateOS how your market is performing. Get answers in plain language, not pivot tables." },
];

const CHIPS = [
  "Show past-due by market",
  "Flag incomplete closeouts",
  "Summarize today's production",
];

export default function AICapabilities() {
  return (
    <section data-testid="ai-capabilities-section" className="section-y relative">
      <div className="absolute -top-20 left-1/4 w-96 h-96 rounded-full bg-[#F97316] mix-blend-screen blur-[160px] opacity-15 pointer-events-none" />
      <div className="container-x">
        <div className="max-w-3xl reveal mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">
            <Sparkles size={12} /> AI Built In
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            AI built into the workflow — not added as an afterthought.
          </h2>
          <p className="mt-5 text-lg text-[#94A3B8] leading-relaxed">
            LocateOS AI works in the background on every ticket, every shift, every market.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
          {/* Left: capability cards */}
          <div className="space-y-3">
            {CAPS.map((c, i) => (
              <div
                key={c.title}
                className="reveal rounded-2xl glass p-5 hover:border-[#F97316]/40 hover:-translate-y-0.5 transition-all"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-[#F97316]/15 border border-[#F97316]/30 grid place-items-center shrink-0 font-mono font-bold text-[#F97316]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-display font-bold text-white">{c.title}</div>
                    <div className="text-sm text-[#94A3B8] mt-1 leading-relaxed">{c.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: AI chat mockup */}
          <div className="reveal relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#F97316]/20 to-[#FB923C]/20 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-2xl glass-strong p-5 border border-white/10">
              <div className="flex items-center gap-3 pb-4 border-b border-white/5">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#F97316] to-[#ea580c] grid place-items-center">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <div className="font-display font-bold text-white">LocateOS AI</div>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#22C55E]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-dot" /> ONLINE
                  </div>
                </div>
                <div className="ml-auto h-24 w-24 -mt-6 -mr-3">
                  <AICrystal className="h-full w-full" />
                </div>
              </div>

              <div className="py-5 space-y-4">
                <div className="flex justify-end">
                  <div className="max-w-[80%] rounded-xl rounded-tr-sm bg-[#FB923C]/25 border border-[#FB923C]/30 px-4 py-2.5 text-sm text-white">
                    Which locators have the highest overdue risk this week?
                  </div>
                </div>
                <div className="flex">
                  <div className="max-w-[88%] rounded-xl rounded-tl-sm bg-[#0a0a0a] border border-white/5 px-4 py-3 text-sm text-[#94A3B8] leading-relaxed">
                    <span className="text-white">Marcus T.</span> (Atlanta) has{" "}
                    <span className="text-[#F97316] font-mono">4 tickets</span> past SLA with no field update.{" "}
                    <span className="text-white">Jordan R.</span> (Nashville) has{" "}
                    <span className="text-[#F97316] font-mono">2 high-complexity</span> tickets unassigned for 18 hours. Recommend immediate dispatch review.
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    data-testid={`ai-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[#94A3B8] hover:text-white hover:border-[#F97316]/40 transition-colors"
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 rounded-xl bg-[#000000] border border-white/10 px-3 py-2.5">
                <input
                  data-testid="ai-input"
                  placeholder="Ask LocateOS anything…"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-[#94A3B8] outline-none"
                />
                <button
                  data-testid="ai-send-btn"
                  className="h-8 w-8 grid place-items-center rounded-lg bg-[#F97316] text-white"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
